import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { Wrench, Plus, X, ArrowLeft, Sparkles, Star } from 'lucide-react';

// Sprint 76 (2/N) — Mesa de Servicio (tickets de garantía). El backend
// (warranty.py, Sprint 75 11/N) llevaba sprints funcionando sin ninguna
// pantalla de tenant que lo usara.
const STATUS_STYLES = {
  open: { label: 'Abierto', bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700' },
  in_progress: { label: 'En proceso', bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700' },
  scheduled: { label: 'Visita agendada', bg: 'bg-violet-50', border: 'border-violet-300', text: 'text-violet-700' },
  resolved: { label: 'Resuelto', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700' },
  closed: { label: 'Cerrado', bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-500' },
};
const CATEGORY_LABELS = { plomeria: 'Plomería', electrico: 'Eléctrico', acabados: 'Acabados', estructural: 'Estructural', electrodomesticos: 'Electrodomésticos', otro: 'Otro' };
const PRIORITY_STYLES = {
  baja: 'bg-gray-100 text-gray-600', media: 'bg-blue-50 text-blue-600',
  alta: 'bg-amber-50 text-amber-700', urgente: 'bg-red-50 text-red-700',
};

export default function WarrantyTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const [leads, setLeads] = useState([]);
  const [newTicket, setNewTicket] = useState({ crm_lead_id: '', subject: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [npsScore, setNpsScore] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/warranty/tickets', { params: statusFilter !== 'all' ? { status: statusFilter } : {} });
      setTickets(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron cargar los tickets'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/tenant/crm/leads').then(r => setLeads(r.data?.items || r.data || [])).catch(() => setLeads([]));
  }, []);

  const openDetail = async (id) => {
    setSelected(id);
    setDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/tenant/warranty/tickets/${id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar el ticket'); }
    finally { setDetailLoading(false); }
  };

  const handleCreate = async () => {
    if (!newTicket.crm_lead_id) { toast.error('Elige el lead/propietario'); return; }
    if (!newTicket.subject.trim()) { toast.error('Describe el asunto del reporte'); return; }
    setSaving(true);
    try {
      await api.post('/tenant/warranty/tickets', newTicket);
      toast.success('Ticket creado — la IA ya lo clasificó por categoría y prioridad');
      setNewTicket({ crm_lead_id: '', subject: '', description: '' });
      setShowNew(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo crear el ticket'); }
    finally { setSaving(false); }
  };

  const handleAddComment = async () => {
    if (!commentBody.trim()) return;
    try {
      await api.post(`/tenant/warranty/tickets/${selected}/comments`, { body: commentBody });
      setCommentBody('');
      openDetail(selected);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo agregar el comentario'); }
  };

  const handleStatusChange = async (status) => {
    try {
      await api.patch(`/tenant/warranty/tickets/${selected}/status`, { status });
      toast.success('Estado actualizado');
      openDetail(selected); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cambiar el estado'); }
  };

  const handleScheduleVisit = async () => {
    if (!visitDate) { toast.error('Elige fecha y hora de la visita'); return; }
    try {
      await api.post(`/tenant/warranty/tickets/${selected}/schedule-visit`, { starts_at: new Date(visitDate).toISOString(), duration_minutes: 60 });
      toast.success('Visita técnica agendada — ya aparece en Agenda');
      setVisitDate('');
      openDetail(selected); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo agendar la visita'); }
  };

  const handleClose = async () => {
    try {
      await api.post(`/tenant/warranty/tickets/${selected}/close`, { nps_score: npsScore === '' ? null : Number(npsScore) });
      toast.success('Ticket cerrado');
      setNpsScore('');
      openDetail(selected); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cerrar el ticket'); }
  };

  if (selected) {
    return (
      <div className="space-y-4" data-testid="warranty-ticket-detail">
        <button onClick={() => { setSelected(null); setDetail(null); }} className="text-xs text-[#64748B] hover:text-[#0F172A] flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Volver a Mesa de Servicio
        </button>
        {detailLoading || !detail ? (
          <p className="text-xs text-[#94A3B8] text-center py-10">Cargando...</p>
        ) : (
          <div className="bg-white border rounded-xl p-4 space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs text-[#94A3B8]">{detail.display_code}</p>
                <h2 className="text-sm font-bold text-[#0F172A]">{detail.subject}</h2>
                <p className="text-xs text-[#64748B] mt-0.5">{detail.lead_name}{detail.unit_name ? ` · ${detail.unit_name}` : ''}</p>
                {detail.description && <p className="text-xs text-[#64748B] mt-1">{detail.description}</p>}
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${(STATUS_STYLES[detail.status] || STATUS_STYLES.open).bg} ${(STATUS_STYLES[detail.status] || STATUS_STYLES.open).border} ${(STATUS_STYLES[detail.status] || STATUS_STYLES.open).text}`}>
                  {(STATUS_STYLES[detail.status] || {}).label || detail.status}
                </span>
                {detail.priority && <span className={`text-[10px] px-2 py-0.5 rounded-full ${PRIORITY_STYLES[detail.priority] || 'bg-gray-100 text-gray-600'}`}>Prioridad {detail.priority}</span>}
                {detail.category && <span className="text-[10px] text-[#94A3B8]">{CATEGORY_LABELS[detail.category] || detail.category}</span>}
                {detail.ai_triaged && (
                  <span className="text-[10px] text-violet-500 flex items-center gap-0.5"><Sparkles className="w-3 h-3" /> Clasificado por IA</span>
                )}
              </div>
            </div>

            {detail.status !== 'closed' && (
              <div className="flex flex-wrap gap-1.5 pt-1 border-t border-[#F1F5F9]">
                {['open', 'in_progress', 'resolved'].filter(s => s !== detail.status).map(s => (
                  <Button key={s} size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStatusChange(s)} data-testid={`warranty-status-${s}`}>
                    Marcar {(STATUS_STYLES[s] || {}).label?.toLowerCase()}
                  </Button>
                ))}
              </div>
            )}

            {detail.status !== 'closed' && detail.status !== 'resolved' && (
              <div className="p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-2">
                <p className="text-xs font-semibold text-[#334155]">Agendar visita técnica</p>
                <div className="flex gap-2">
                  <Input type="datetime-local" value={visitDate} onChange={e => setVisitDate(e.target.value)} className="h-7 text-xs" data-testid="warranty-visit-date" />
                  <Button size="sm" className="h-7 text-xs" onClick={handleScheduleVisit} data-testid="warranty-schedule-visit">Agendar</Button>
                </div>
              </div>
            )}

            {(detail.status === 'resolved' || detail.status !== 'closed') && (
              <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
                <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1"><Star className="w-3.5 h-3.5" /> Cerrar ticket con NPS</p>
                <div className="flex gap-2 items-center">
                  <Input type="number" min="0" max="10" placeholder="0-10 (opcional)" value={npsScore} onChange={e => setNpsScore(e.target.value)} className="h-7 text-xs w-40" data-testid="warranty-nps-score" />
                  <Button size="sm" className="h-7 text-xs" onClick={handleClose} data-testid="warranty-close-ticket">Cerrar ticket</Button>
                </div>
              </div>
            )}
            {detail.status === 'closed' && detail.nps_score != null && (
              <p className="text-xs text-[#64748B]">NPS registrado: <strong>{detail.nps_score}</strong>/10</p>
            )}

            <div className="space-y-2 pt-2 border-t border-[#F1F5F9]">
              <p className="text-xs font-semibold text-[#334155]">Comentarios</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {(detail.comments || []).length === 0 && <p className="text-[11px] text-[#94A3B8]">Sin comentarios todavía.</p>}
                {(detail.comments || []).map(c => (
                  <div key={c.id} className="text-xs bg-[#F8FAFC] rounded-lg px-2.5 py-1.5">
                    <p className="text-[#334155]">{c.body}</p>
                    <p className="text-[10px] text-[#94A3B8] mt-0.5">{c.author_type === 'staff' ? 'Equipo' : c.author_type} · {c.created_at ? new Date(c.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : ''}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input placeholder="Escribe una nota..." value={commentBody} onChange={e => setCommentBody(e.target.value)} className="h-7 text-xs" data-testid="warranty-comment-input" />
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleAddComment} data-testid="warranty-comment-save">Agregar</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="warranty-tickets-page">
      <div>
        <h2 className="text-sm font-bold flex items-center gap-2">
          <Wrench className="w-4 h-4" /> Mesa de Servicio
          <HelpTooltip text="Tickets de garantía reportados por propietarios: falla plomería/eléctrico/acabados. La IA clasifica categoría y prioridad automáticamente al crear el ticket." />
        </h2>
        <p className="text-xs text-[#64748B] mt-0.5">Da seguimiento a reportes de garantía post-entrega, agenda visitas técnicas y cierra con satisfacción (NPS).</p>
      </div>

      <SectionHelp
        module="warranty_tickets"
        title="Mesa de Servicio"
        description="Cuando un propietario reporta una falla (por WhatsApp, llamada, o aquí manualmente), se crea un ticket con categoría y prioridad clasificadas por IA. Desde aquí agendas la visita técnica (aparece en Agenda) y cierras con una calificación de satisfacción."
        whatItDoesNot="No reemplaza Agenda — la visita técnica se agenda aquí pero se administra como cualquier otra cita."
      />

      <div className="flex items-center justify-between gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 text-xs w-48" data-testid="warranty-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS_STYLES).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => setShowNew(!showNew)} data-testid="new-warranty-ticket-btn">
          <Plus className="w-4 h-4 mr-1" /> Reportar falla
        </Button>
      </div>

      {showNew && (
        <div className="bg-white border rounded-xl p-4 space-y-2">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-semibold">Nuevo ticket de garantía</h4>
            <button onClick={() => setShowNew(false)}><X className="w-4 h-4 text-[#94A3B8]" /></button>
          </div>
          <div>
            <label className="text-xs text-[#64748B]">Propietario</label>
            <Select value={newTicket.crm_lead_id} onValueChange={v => setNewTicket({ ...newTicket, crm_lead_id: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="new-warranty-lead"><SelectValue placeholder="Elige un lead..." /></SelectTrigger>
              <SelectContent>
                {leads.map(l => <SelectItem key={l.id} value={l.id}>{l.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#64748B]">Asunto</label>
            <Input placeholder="Fuga de agua en baño principal" value={newTicket.subject}
              onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })} data-testid="new-warranty-subject" />
          </div>
          <div>
            <label className="text-xs text-[#64748B]">Descripción (opcional)</label>
            <Input placeholder="Detalles adicionales..." value={newTicket.description}
              onChange={e => setNewTicket({ ...newTicket, description: e.target.value })} data-testid="new-warranty-description" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="new-warranty-save">
              {saving ? 'Guardando...' : 'Crear ticket'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-[#94A3B8] text-center py-10">Cargando...</p>
      ) : tickets.length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center">
          <Wrench className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Todavía no hay tickets de garantía. Repórtalos aquí o dejar que la IA los capture desde WhatsApp/llamada.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="warranty-tickets-list">
          {tickets.map(t => {
            const style = STATUS_STYLES[t.status] || STATUS_STYLES.open;
            return (
              <button key={t.id} onClick={() => openDetail(t.id)}
                className="w-full bg-white border rounded-xl p-3 text-left hover:border-[#0F172A] transition-colors flex items-center justify-between gap-2"
                data-testid={`warranty-ticket-card-${t.id}`}>
                <div className="min-w-0">
                  <p className="text-xs text-[#94A3B8]">{t.display_code}</p>
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{t.subject}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{t.lead_name}{t.unit_name ? ` · ${t.unit_name}` : ''}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${style.bg} ${style.border} ${style.text}`}>{style.label}</span>
                  {t.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${PRIORITY_STYLES[t.priority] || 'bg-gray-100 text-gray-600'}`}>{t.priority}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
